import React, { useEffect, useState } from 'react';
import { HiTrash, HiChevronRight, HiPlus } from 'react-icons/hi';
import apiClient from '../api/client';
import { useNotification } from '../context/NotificationContext';
import { useWbNavigate } from './WbRouteContext';
import WbShell, { WbTopBar } from './WbShell';
import { clearAuth } from '../utils/auth';

function ClientCard({ client, onOpen, onDelete, onOpenHub }) {
  return (
    <div className="wb-card wb-card-hover p-5 group flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={() => onOpen(client.id)}
        className="flex-1 min-w-0 text-left"
      >
        <h3 className="text-base font-semibold truncate group-hover:text-[var(--wb-text)] transition-colors">
          {client.name}
          {client.is_demo ? (
            <span className="ml-2 text-xs font-normal text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
              демо
            </span>
          ) : null}
        </h3>
        <p className="text-sm text-[var(--wb-text-muted)] mt-0.5">
          {client.is_demo ? 'Демо · WB + Ozon + Авито' : client.description || 'Кабинет Wildberries'}
        </p>
      </button>
      <div className="flex items-center gap-2 shrink-0">
        {client.is_demo && onOpenHub && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenHub(client.id);
            }}
            className="text-xs wb-btn-secondary px-2 py-1"
          >
            Мульти-канал
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={(e) => onDelete(e, client.id, client.name)}
            className="p-2 rounded-lg text-[var(--wb-danger)] hover:bg-[var(--wb-danger-bg)] transition-colors"
            aria-label={`Удалить ${client.name}`}
          >
            <HiTrash className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onOpen(client.id)}
          className="p-1 text-[var(--wb-text-muted)] group-hover:text-[var(--wb-text)]"
          aria-label={`Открыть ${client.name}`}
        >
          <HiChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default function WbClientDashboard() {
  const { confirm } = useNotification();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [showForm, setShowForm] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const navigate = useWbNavigate();

  const isAdmin = userRole === 'admin';
  const canManageClients = userRole === 'admin' || userRole === 'user';

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    apiClient
      .get('/users/me')
      .then(({ data }) => setUserRole(data?.user?.role || null))
      .catch(() => setUserRole(null));
    fetchClients();
  }, [navigate]);

  const fetchClients = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await apiClient.get('/clients');
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
        return;
      }
      setLoadError(err.response?.data?.message || 'Не удалось загрузить клиентов');
    } finally {
      setLoading(false);
    }
  };

  const notify = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
  };

  const handleSelectClient = (clientId) => {
    localStorage.setItem('clientId', clientId);
    navigate(`/client/${clientId}`);
  };

  const handleOpenHub = (clientId) => {
    localStorage.setItem('clientId', clientId);
    navigate(`/hub/${clientId}`);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!name.trim() || !apiToken.trim()) {
      notify('Введите название и API-токен', 'error');
      return;
    }
    try {
      const { data } = await apiClient.post('/clients', { name, api_token: apiToken });
      notify(data?.message || 'Клиент создан', 'success');
      setName('');
      setApiToken('');
      setShowForm(false);
      fetchClients();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка создания', 'error');
    }
  };

  const handleDeleteClient = async (e, clientId, clientName) => {
    e.stopPropagation();
    const ok = await confirm({
      message: `Удалить «${clientName}»?`,
      danger: true,
      confirmText: 'Удалить',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/clients/${clientId}`);
      notify('Клиент удалён', 'success');
      fetchClients();
    } catch (err) {
      notify(err.response?.data?.message || 'Ошибка удаления', 'error');
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const msgClass =
    messageType === 'error'
      ? 'text-[var(--wb-danger)] bg-[var(--wb-danger-bg)]'
      : messageType === 'success'
      ? 'text-[var(--wb-success)] bg-[var(--wb-success-bg)]'
      : 'text-[var(--wb-primary)] bg-[var(--wb-primary-soft)]';

  return (
    <WbShell>
      <WbTopBar
        title="Кабинеты"
        subtitle="Выберите магазин или добавьте новый"
        onLogout={handleLogout}
      />

      <main className="max-w-3xl mx-auto p-6 space-y-6">
        {isAdmin && (
          <div className="flex justify-end gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigate('/admin/script-errors')}
              className="wb-btn-secondary text-sm"
            >
              Ошибки скриптов
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/access')}
              className="wb-btn-secondary text-sm"
            >
              Управление доступами
            </button>
          </div>
        )}

        {message && (
          <p className={`text-sm rounded-lg px-4 py-2.5 ${msgClass}`} role="status">
            {message}
          </p>
        )}

        {loading ? (
          <div className="wb-card p-10 text-center text-[var(--wb-text-muted)]">Загрузка...</div>
        ) : loadError ? (
          <div className="wb-card p-8 text-center space-y-3">
            <p className="text-[var(--wb-danger)]">{loadError}</p>
            <button type="button" onClick={fetchClients} className="wb-btn">
              Повторить
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-[var(--wb-text-secondary)]">
                {clients.length}{' '}
                {clients.length === 1 ? 'кабинет' : clients.length < 5 ? 'кабинета' : 'кабинетов'}
              </p>
              {canManageClients && (
                <button type="button" onClick={() => setShowForm((v) => !v)} className="wb-btn">
                  <HiPlus className="w-4 h-4" />
                  Добавить
                </button>
              )}
            </div>

            {showForm && canManageClients && (
              <form onSubmit={handleCreateClient} className="wb-card p-6 space-y-4">
                <h2 className="font-semibold">Новый кабинет WB</h2>
                <div>
                  <label htmlFor="wb-client-name" className="block text-sm font-medium mb-1.5">
                    Название
                  </label>
                  <input
                    id="wb-client-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="wb-input"
                    placeholder="Мой магазин"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="wb-client-token" className="block text-sm font-medium mb-1.5">
                    API-токен Wildberries
                  </label>
                  <input
                    id="wb-client-token"
                    type="text"
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    className="wb-input font-mono text-xs"
                    placeholder="eyJhbGciOi..."
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="wb-btn">
                    Создать
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="wb-btn-secondary">
                    Отмена
                  </button>
                </div>
              </form>
            )}

            {clients.length === 0 ? (
              <div className="wb-card p-10 text-center">
                <p className="text-[var(--wb-text-muted)] mb-4">
                  {canManageClients ? 'Кабинетов пока нет' : 'Вам не назначены кабинеты. Обратитесь к администратору.'}
                </p>
                {canManageClients && (
                  <button type="button" onClick={() => setShowForm(true)} className="wb-btn">
                    Добавить первый кабинет
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {clients.map((client) => (
                  <ClientCard
                    key={client.id}
                    client={client}
                    onOpen={handleSelectClient}
                    onOpenHub={handleOpenHub}
                    onDelete={canManageClients ? handleDeleteClient : undefined}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </WbShell>
  );
}
